import { History } from 'history';
import { useAppSelector, useHistory } from 'src/utils/Hooks';

interface SearchParameters {
  [key: string]: string | string[];
}

/**
 * Creates an interface to programmatically build URLs with search parameters and optionally navigate to them.
 */
export class BeaconNavigator {
  /**
   * URLSearchParams object to store search parameters
   */
  private searchParams: URLSearchParams;

  /**
   * URLSearchParams object to store default search parameters in case we want to reset the search params to their initial state
   */
  private defaultSearchParams: URLSearchParams;

  /**
   * Pathname to be used in the URL. If not set, the current pathname will be used
   */
  private pathname: string;

  /**
   * Base path to be used in the URL. If not set, the current pathname will be used
   */
  private basePath: string;

  /**
   * Function to navigate to a new URL. If not set during instantiation, the `go` method will not work.
   */
  public go: null | ((param?: string) => void);

  constructor(init?: URLSearchParams, history?: History, basePath = '') {
    const searchParams = init ?? new URLSearchParams();

    this.searchParams = searchParams;
    this.defaultSearchParams = new URLSearchParams(searchParams);
    this.basePath = basePath;

    if (history) {
      this.initialize(history);
    }
  }

  /**
   *
   * @param params Object containing search parameters to update.
   * Updates the search parameters with the provided parameters or add them if they don't exist.
   */
  public updateSearchParams(params: SearchParameters) {
    Object.keys(params).forEach((key) => {
      const values = params[key];
      if (Array.isArray(values)) {
        if (values.length) {
          this.searchParams.set(key, values.join(','));
        }
      } else {
        this.searchParams.set(key, values);
      }
    });
    return this;
  }

  /**
   *
   * @param params Object containing search parameters to add.
   * Appends the provided parameters to the search parameters.
   * If the parameter already exists, it will append the new values to the existing ones.
   * @example addSearchParams({ tab: ['traffic'], subtab: ['totalClicks'] }); // ?tab=traffic&subtab=totalClicks
   */
  public addSearchParams(params: SearchParameters) {
    Object.keys(params).forEach((key) => {
      if (!key) {
        return;
      }

      const values = params[key];

      if (Array.isArray(values)) {
        if (values.length) {
          this.searchParams.append(key, values.join(','));
        }
      } else {
        this.searchParams.append(key, values);
      }
    });
    return this;
  }

  public back() {
    window.history.back();
    return this;
  }

  /**
   * Clears the search parameters property and initializes it with a new URLSearchParams object
   */
  public clearSearchParams() {
    this.searchParams = new URLSearchParams();
    return this;
  }

  /**
   * Resets the search parameters to their initial instantiation state
   */
  public resetSearchParams() {
    this.searchParams = this.defaultSearchParams;
    return this;
  }

  /**
   * @param pathname String to set the pathname in the URL
   * Sets the pathname in the URL
   */
  public setPathname(pathname: string) {
    // Ensure pathname starts with a forward slash
    this.pathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return this;
  }

  /**
   *
   * @returns String with the URL built from the search parameters and pathname
   */
  public build() {
    // If no explicit pathname is set, use the current path
    let path = this.pathname ?? window.location.pathname;

    // If we have a basePath and the pathname doesn't start with it,
    // prepend the basePath to ensure correct routing
    if (this.basePath && !path.startsWith(this.basePath)) {
      path = `${this.basePath}${path}`;
    }

    const searchParams = this.searchParams.toString();
    const url = `${path}${searchParams ? `?${searchParams}` : ''}`;
    return url;
  }

  /**
   * Opens the current or provided URL in a new browser tab.
   * If `param` is provided, it will be used directly; otherwise it uses the value from `build()`.
   * Returns `this` to allow method chaining.
   */
  public openInNewTab(param?: string) {
    const url = param ?? this.build();
    // Ensure absolute URL to avoid base/href edge cases
    const absoluteUrl = url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `${window.location.origin}${url}`;
    const newWindow = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    // Optionally focus if the popup wasn't blocked
    if (newWindow && typeof newWindow.focus === 'function') {
      newWindow.focus();
    }
    return this;
  }

  /**
   * Initializes the `go` method to navigate to a new URL if a history object is provided.
   * @param history History object to navigate to the new URL
   */
  private initialize(history: History) {
    this.go = (param?: string) => {
      if (!history) {
        console.error(
          'History object is not defined; initialize BeaconNavigator with a history object before calling `go` method'
        );
        return;
      }

      if (param) {
        history.push(param);
      } else {
        const newUrl = this.build();
        history.push(newUrl);
      }
    };
  }
}

/**
 * Hook to create a BeaconNavigator object with default search parameters already set.
 * @returns BeaconNavigator object
 */
export const useBeaconNavigator = () => {
  const history = useHistory();
  const searchParams = new URLSearchParams();
  const retailerId = useAppSelector((state) => state.retailer.id);
  const mainPeriodId = useAppSelector((state) => state.mainTimePeriod.id);
  const comparisonPeriodId = useAppSelector((state) => state.comparisonTimePeriod.id);
  const startWeek = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeek = useAppSelector((state) => state.mainTimePeriod.endWeek);
  const startDay = useAppSelector((state) => state.mainTimePeriod.startDayId);
  const endDay = useAppSelector((state) => state.mainTimePeriod.endDayId);

  // Base URL Search Params
  searchParams.append('rid', retailerId);
  searchParams.append('wr', mainPeriodId);
  searchParams.append('pid', comparisonPeriodId);
  searchParams.append('sw', `${startWeek}`);
  searchParams.append('ew', `${endWeek}`);
  searchParams.append('sdid', `${startDay}`);
  searchParams.append('edid', `${endDay}`);

  const beaconNavigator = new BeaconNavigator(searchParams, history);

  return { beaconNavigator };
};
